import { Injectable } from '@angular/core';
import { ApiService } from '../../http/api.service';
import { Observable, of } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CommonOptions, City, DaneCity, Requirements, RequirementsContract, RequirementsProyect, ContractIdProyect, SecurityReferences } from '../../interfaces/interfaces';
import { NumberCountry } from '../../constants/number-city.constant';
import { SearchEmployerIdCompanyInfoId } from '../../models/Common/searchEmployerIdCompanyInfoId';
import {User, UserInfo} from '../../models/User';
import { catchError, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class CommonService {

    constructor(
        private api: ApiService,
        private _http: HttpClient
    ) { }

    getUserInfo(): Observable<UserInfo> {
        return this._http.get<UserInfo>(this.api.getUrl(`api/userinfo`));
    }

    getRolesCampany(CompanyId) {
        return this._http.get(this.api.getUrl(`api/Roles/Company/${CompanyId}?company=true`));
    }

    getRolesCampanyAll(CompanyId) {
        return this._http.get(this.api.getUrl(`api/Roles/Company/${CompanyId}`));
    }

    getRoles() {
        return this._http.get(this.api.getUrl(`api/Roles`));
    }

    getPermissions() {
        return this._http.get(this.api.getUrl(`api/Permissions`));
    }


    private fetchCommonOptionsApi(typeCommonOptions: string, token?): Observable<CommonOptions[]> {
        const url = this.api.getUrl('api/CommonOptions?Type=' + typeCommonOptions);
        if (token !== undefined) {
            const headers = new HttpHeaders({
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            });
            return this._http.get<CommonOptions[]>(url, { headers });
        } else {
            return this._http.get<CommonOptions[]>(url);
        }
    }

    // GET CommonOptions
    getCommonOptions(typeCommonOptions: string, token?): Observable<CommonOptions[]> {
        if (token !== undefined) {
            return this.fetchCommonOptionsApi(typeCommonOptions, token);
        }
        return this.getCommonOptionsLocalOrApi(typeCommonOptions);
    }

  getUserInfoWithToken(token?): Observable<UserInfo> {
    const url = this.api.getUrl('api/userinfo');
    if (token !== undefined) {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      });
      return this._http.get<UserInfo>(url, { headers });
    } else {
      return this._http.get<UserInfo>(url);
    }
  }

    getCommonOptionsLocal(typeCommonOptions: string): CommonOptions[] {
        const storageKey = typeCommonOptions.startsWith('CoPs_') ? typeCommonOptions : `CoPs_${typeCommonOptions}`;
        const raw = localStorage.getItem(storageKey) || localStorage.getItem(typeCommonOptions);
        if (!raw) {
            return [];
        }

        try {
            const options = JSON.parse(raw) as CommonOptions[];
            return Array.isArray(options) ? options : [];
        } catch {
            return [];
        }
    }

    getCommonOptionsLocalOrApi(typeCommonOptions: string): Observable<CommonOptions[]> {
        const storageKey = typeCommonOptions.startsWith('CoPs_') ? typeCommonOptions : `CoPs_${typeCommonOptions}`;
        const localOptions = this.getCommonOptionsLocal(storageKey);
        if (localOptions.length > 0) {
            return of(localOptions);
        }

        const apiType = typeCommonOptions.startsWith('CoPs_')
            ? typeCommonOptions.replace('CoPs_', '')
            : typeCommonOptions;

        return this.fetchCommonOptionsApi(apiType).pipe(
            tap((options: CommonOptions[]) => {
                try {
                    localStorage.setItem(storageKey, JSON.stringify(options || []));
                } catch (e) {
                    console.error('Error al guardar CommonOptions en localStorage', e);
                }
            }),
            catchError(() => of([] as CommonOptions[]))
        );
    }

    getCitiesLocal(numbercContry?: NumberCountry | string | number): City[] {
        const country = numbercContry !== undefined && numbercContry !== null ? numbercContry : 0;
        const raw = localStorage.getItem(`CoPs_Cities_${country}`);
        if (!raw) {
            return [];
        }

        try {
            const cities = JSON.parse(raw);
            return Array.isArray(cities) ? cities : [];
        } catch {
            return [];
        }
    }

    clearCitiesLocal(numbercContry?: NumberCountry | string | number): void {
        try {
            if (numbercContry !== undefined && numbercContry !== null) {
                localStorage.removeItem(`CoPs_Cities_${numbercContry}`);
            } else {
                for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i);
                    if (key && key.startsWith('CoPs_Cities_')) {
                        localStorage.removeItem(key);
                    }
                }
            }
        } catch (e) { }
    }

    // GET Cities * country
    getCities(numbercContry: NumberCountry | string | number): Observable<City[]> {
        const country = numbercContry !== undefined && numbercContry !== null ? numbercContry : 0;
        const local = this.getCitiesLocal(country);
        if (local.length > 0) {
            return of(local);
        }

        const url = this.api.getUrl('api/City?country=' + numbercContry);
        return this._http.get<City[]>(url).pipe(
            tap((cities: City[]) => {
                if (Array.isArray(cities) && cities.length > 0) {
                    try {
                        localStorage.setItem(`CoPs_Cities_${numbercContry}`, JSON.stringify(cities));
                    } catch (e) {
                        console.error('Error al guardar Cities en localStorage', e);
                    }
                }
            }),
            catchError(() => of([]))
        );
    }

    getDaneCityLocal(): DaneCity[] {
        const raw = localStorage.getItem('CoPs_DaneCity');
        if (!raw) {
            return [];
        }

        try {
            const daneCity = JSON.parse(raw);
            return Array.isArray(daneCity) ? daneCity : [];
        } catch {
            return [];
        }
    }

    clearDaneCityLocal(): void {
        try {
            localStorage.removeItem('CoPs_DaneCity');
        } catch (e) { }
    }

    getDaneCity(): Observable<DaneCity[]> {
        const local = this.getDaneCityLocal();
        if (local.length > 0) {
            return of(local);
        }

        return this._http.get<DaneCity[]>(this.api.getUrl(`api/DaneCity`)).pipe(
            tap((cities: DaneCity[]) => {
                if (Array.isArray(cities) && cities.length > 0) {
                    cities.sort((a, b) => (a.CityName || '').localeCompare(b.CityName || '', 'es', { sensitivity: 'base' }));
                    try {
                        localStorage.setItem('CoPs_DaneCity', JSON.stringify(cities));
                    } catch (e) {
                        console.error('Error al guardar DaneCity en localStorage', e);
                    }
                }
            }),
            catchError(() => of([]))
        );
    }

    getDaneCitySelectTrue(): Observable<any> {
        const local = this.getDaneCityLocal();
        if (local.length > 0) {
            return of(local);
        }

        return this._http.get<any>(this.api.getUrl(`api/DaneCity?Select=true`)).pipe(
            tap((cities: any) => {
                if (Array.isArray(cities) && cities.length > 0) {
                    cities.sort((a, b) => (a.CityName || '').localeCompare(b.CityName || '', 'es', { sensitivity: 'base' }));
                    try {
                        localStorage.setItem('CoPs_DaneCity', JSON.stringify(cities));
                    } catch (e) {
                        console.error('Error al guardar DaneCity en localStorage', e);
                    }
                }
            }),
            catchError(() => of([]))
        );
    }

    getTownCity(cityCode) {
      return this._http.get(this.api.getUrl(`api/Town/City/${cityCode}`));
  }

    // Search Employer
    getSearchEmployerIdCompanyInfoId(companyInfoId: string, name: any): Observable<SearchEmployerIdCompanyInfoId> {
        return this._http.get<SearchEmployerIdCompanyInfoId>(this.api.getUrl(`api/Employer?companyInfoId=${companyInfoId}&name=${name}`));
    }

    // Search Employer new AllEmployers
    getSearchAllEmployersCompanyInfoId(companyInfoId: string, name: any): Observable<SearchEmployerIdCompanyInfoId> {
        return this._http.get<SearchEmployerIdCompanyInfoId>(this.api.getUrl(`api/Employer/AllEmployers?documentName=${name}`));
    }

    // Search Employer new AllEmployers Witch companyInfoId
    getSearchAllEmployersWitchAdministrador(companyInfoId: string, name: any): Observable<SearchEmployerIdCompanyInfoId> {
        return this._http.get<SearchEmployerIdCompanyInfoId>(this.api.getUrl(`api/Employer/AllEmployers?companyInfoId=${companyInfoId}&documentName=${name}`));
    }

    getAllEmployersCompanyId(companyInfoId): Observable<SearchEmployerIdCompanyInfoId> {
        return this._http.get<SearchEmployerIdCompanyInfoId>(this.api.getUrl(`api/Employer/AllEmployers?companyId=${companyInfoId}`));
    }

    getSearchEmployerDocumentTypeName(documentType: string, name: any): Observable<SearchEmployerIdCompanyInfoId> {
        return this._http.get<SearchEmployerIdCompanyInfoId>(this.api.getUrl(`api/Employer?documentType=${documentType}&name=${name}`));
    }

    // Requirements for Company
    getRequirements(companyInfoId: string): Observable<Requirements[]>  {
        return this._http.get<Requirements[]>(this.api.getUrl(`api/Requirement?companyId=${companyInfoId}`));
    }

    // Requirements for all
    getRequirementsAll(): Observable<Requirements[]>  {
        return this._http.get<Requirements[]>(this.api.getUrl(`api/Requirement`));
    }

    // Requirements certificables para el Gestor de Documentos de Personal (usados en SOLICITUD DE DOCUMENTO)
    // byDate=true se usa en cargue masivo para FECHA DE UN REQUISITO (typeShowDialog === 3)
    getRequirementsCertPersonal(byDate: boolean = false): Observable<Requirements[]>  {
        let url = `api/Requirement?byCertification=true&byPersonal=true&byEmployerPersonal=true&excludeBySecuritySocial=true`;
        if (byDate === true) {
            url += `&byDate=true`;
        }
        return this._http.get<Requirements[]>(this.api.getUrl(url));
    }

    // Requirements certificables para el Gestor de Documentos de Empresas (usados en SOLICITUD DE DOCUMENTO)
    getRequirementsCertEmployer(): Observable<Requirements[]>  {
        return this._http.get<Requirements[]>(this.api.getUrl(`api/Requirement?byEmployer=true&byCertification=true`));
    }

    // Requirements CONTRACT
    getRequirementsContract(contractId: string): Observable<RequirementsContract[]>  {
        return this._http.get<RequirementsContract[]>(this.api.getUrl(`api/Contract/${contractId}/Requirements`));
    }

    // Requirements PROYECT
    getRequirementsProyect(proyectId: string): Observable<RequirementsProyect[]>  {
        return this._http.get<RequirementsProyect[]>(this.api.getUrl(`api/Project/${proyectId}/Requirements`));
    }

    // Contract Id Proyect
    getContractIdProyect(contractId: string): Observable<ContractIdProyect[]>  {
        return this._http.get<ContractIdProyect[]>(this.api.getUrl(`api/Contract/${contractId}/Project`));
    }

    getTypeSecurityReferences(typeValue): Observable<SecurityReferences[]>  {
        return this._http.get<SecurityReferences[]>(this.api.getUrl(`api/SecurityReferences?Type=${typeValue}`));
    }

    getSecurityReferences() {
        return this.api.get('api/SecurityReferences');
    }

    getJobsLocal(): any[] {
        const raw = localStorage.getItem('CoPs_Jobs');
        if (!raw) {
            return [];
        }

        try {
            const jobs = JSON.parse(raw);
            return Array.isArray(jobs) ? jobs : [];
        } catch {
            return [];
        }
    }

    clearJobsLocal(): void {
        try {
            localStorage.removeItem('CoPs_Jobs');
        } catch (e) { }
    }

    getJobs(): Observable<any> {
        const local = this.getJobsLocal();
        if (local.length > 0) {
            return of(local);
        }

        return this._http.get<any[]>(this.api.getUrl('api/Job')).pipe(
            tap((jobs: any[]) => {
                if (Array.isArray(jobs) && jobs.length > 0) {
                    jobs.sort((a, b) => (a.Name || '').localeCompare(b.Name || '', 'es', { sensitivity: 'base' }));
                    try {
                        localStorage.setItem('CoPs_Jobs', JSON.stringify(jobs));
                    } catch (e) {
                        console.error('Error al guardar jobs en localStorage', e);
                    }
                }
            }),
            catchError(() => of([]))
        );
    }


    getProyect() {
        return this._http.get(this.api.getUrl(`api/Project`));
    }

    getContract(subcontractors?: boolean) {
        const url = subcontractors ? `api/Contract?subcontractors=true` : `api/Contract`;
        return this._http.get(this.api.getUrl(url));
    }

    /**
     * Permite obtener las sucursales de company/employer
     */
    getBranchOffice() {
        return this._http.get(this.api.getUrl(`api/BranchOffice`));
    }

    /**
     * Permite obtener las sucursales de company/employer
     */
    postBranchOffice(model) {
        return this._http.post(this.api.getUrl(`api/BranchOffice`), model);
    }


    /**
     * Permite actualizar las sucursales de company/employer
     */
    putBranchOffice(Id, model) {
        return this._http.put(this.api.getUrl(`api/BranchOffice/${Id}`), model);
    }


    /**
     * Permite eliminar las sucursales de company/employer
     */
    deleteBranchOffice(Id) {
        return this._http.delete(this.api.getUrl(`api/BranchOffice/${Id}`));
    }

    /**
     * Permite obtener las EconomicActivity de company/employer
     */
     getEconomicActivity() {
        return this._http.get(this.api.getUrl(`api/EconomicActivity?Select=true`));
    }

    /**
     * Permite obtener los PositionType
     * @param personalContractId - ID del contrato personal para filtrar (opcional)
     * - Si no se envía o es null/undefined: retorna todos
     * - Si es 0: se trata como "no enviado" = retorna todos
     * - Si es un número válido: filtra por ese personalContractId
     * @param categoryId
     */
    getPositionType(personalContractId?: number, categoryId?: number) {
        let url = 'api/Requirement/PositionType';
        if (personalContractId !== undefined && personalContractId !== null && personalContractId !== 0) {
            url += `?personalContractId=${personalContractId}`;
        }
        if (categoryId !== undefined && categoryId !== null && categoryId !== 0) {
            url += personalContractId ? `&category=${categoryId}` : `?category=${categoryId}`;
        }
        return this._http.get(this.api.getUrl(url));
    }

    /**
     * Permite guardar los PositionType por administradora
     */
    postPositionType(model) {
        return this._http.post(this.api.getUrl(`api/Requirement/PositionType`), model);
    }

    /**
     * Permite actualizar los PositionType
     */
    putPositionType(positionTypeId, model) {
        return this._http.put(this.api.getUrl(`api/Requirement/PositionType/${positionTypeId}`), model);
    }

    /**
     * Permite ver lista completa requirement
     * @param companyId ID de la administradora (opcional)
     * @param byEmployer Filtrar por empresa (opcional)
     */
    getRequirement(companyId?: string | number, byEmployer?: boolean) {
        let url = `api/Requirement`;
        const params: string[] = [];

        if (companyId !== undefined && companyId !== null) {
            params.push(`companyId=${companyId}`);
        }
        if (byEmployer === true) {
            params.push(`byEmployer=true`);
        }

        if (params.length > 0) {
            url += `?${params.join('&')}`;
        }

        return this._http.get(this.api.getUrl(url));
    }

    /**
     * Permite actualizar los requerimientos por adminsitrador y tipo de cargo
     */
    putRequirementPositionType(positionTypeId, model) {
        return this._http.put(this.api.getUrl(`api/Requirement/PositionType/Reqs/${positionTypeId}`), model);
    }

    /**
     * Permite filtrar los requerimientos por tipo de cargo
     */
    getDocumentRequirementFilterTypePosition(contractId, personalCompanyInfoId, positionTypeId, requimentId?,  soc: boolean = false ) {
      let url = `api/Requirement/DocumentRequirement/${contractId}/${personalCompanyInfoId}?positionTypeId=${positionTypeId}`;
      if (requimentId != null && requimentId !== '') {
        url += `&personalContractStageId=${requimentId}`;
      }
      if (soc === true) {
        url += `&soc=true`;
      }
      return this._http.get(this.api.getUrl(url));
    }

  /**
   * Permite filtrar los requerimientos por tipo de cargo
   */
  getDocumentRequirementFilterPosition(contractId, personalCompanyInfoId, requimentId?,  soc: boolean = false ) {
    let url = `api/Requirement/DocumentRequirement/${contractId}/${personalCompanyInfoId}?`;
    if (requimentId != null && requimentId !== '') {
      url += `personalContractStageId=${requimentId}`;
    }
    if (soc === true) {
      url += `${(requimentId != null && requimentId !== '') ? '&' : ''}soc=true`;
    }
    return this._http.get(this.api.getUrl(url));
  }

  /**
   * Carga los documentos fijos (Identidad y Contrato Individual de Trabajo)
   */
  getDocumentRequirementFixedDocument(personalCompanyInfoId: any, employerInfoId: any, personalContractStageId?: any, personalTerminationRequestId?: any) {
    let url = `api/Requirement/DocumentRequirement/FixedDocument/${personalCompanyInfoId}/${employerInfoId}`;
    let params = [];
    if (personalContractStageId) {
      params.push(`personalContractStageId=${personalContractStageId}`);
    }
    if (personalTerminationRequestId) {
      params.push(`personalTerminationRequestId=${personalTerminationRequestId}`);
    }
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }
    return this._http.get<Requirements[]>(this.api.getUrl(url));
  }

  /**
   * Carga los documentos requeridos usando el snapshot del stage (flujo JobType).
   * El parámetro jobType=true hace que el backend ignore positionTypeId y retorne
   * únicamente los requirements del snapshot PersonalContractStageRequirements.
   */
  getDocumentRequirementFilterJobType(contractId, personalCompanyInfoId, stageId) {
    return this._http.get(this.api.getUrl(
      `api/Requirement/DocumentRequirement/${contractId}/${personalCompanyInfoId}?personalContractStageId=${stageId}&soc=true&jobType=true`
    ));
  }

    /**
     * Permite filtrar los requerimientos por tipo de retiro
     */
    getDocumentRequirementFilterTerminationType(personalEmployerSnapshotId, terminationType) {
        return this._http.get(this.api.getUrl(`api/Requirement/DocumentRequirement/TerminationType/Snapshot/${personalEmployerSnapshotId}?terminationType=${terminationType}`));
    }

    putDocumentRequirementFilterTypeofWithdrawal(terminationId, model) {
        return this._http.put(this.api.getUrl(`api/PersonalTerminationRequest/Simple/${terminationId}`), model);
    }

    getRolesCompanyProject(companyId, type) {
        return this._http.get(this.api.getUrl(`api/Roles/Company/${companyId}?${type !== '' ? type + '=true' :  ''}`));
    }

    /**
     * Permite traer los permisos por type /NOTA: esta pegando al servidor de ANGULAR JS no a las apis tradicionar que se utiliza
     */
     getPermissionGUIType(PermissionType) {
        return this._http.get(this.api.getUrl(`api/Permissions/PermissionGui`));
    }

    /**
     * Permite traer roles por id
     */
    getRolesId(id) {
        return this._http.get(this.api.getUrl(`api/Roles/${id}`));
    }


    // CRUDPermissions
    /**
     * Permite actualizar los permisos
     */
     putPermissions(id, model) {
        return this._http.put(this.api.getUrl(`api/Permissions/${id}`), model);
    }
    /**
     * Permite guardar lospermisos
     */
     postPermissions(model) {
        return this._http.post(this.api.getUrl(`api/Permissions`), model);
    }

    /**
     * Permite eliminar los permisos
     */
    deletePermissions(id) {
        return this._http.delete(this.api.getUrl(`api/Permissions/${id}`));
    }


    /**
     * Services modulo administracion roles
     */
    postRoles(model)  {
        return this._http.post(this.api.getUrl(`api/Roles`), model);
    }

    putRoles(Id, model)  {
        return this._http.put(this.api.getUrl(`api/Roles/${Id}`), model);
    }

    putRoleStatus(RolId, CompanyId, model)  {
        return this._http.put(this.api.getUrl(`api/Roles/${RolId}/Company/${CompanyId}`), model);
    }

    postRolesCompany(CompanyId, model)  {
        return this._http.post(this.api.getUrl(`api/Roles/Company/${CompanyId}`), model);
    }

    putRolesCompany(IdRol, CompanyId, model)  {
        return this._http.put(this.api.getUrl(`api/Roles/${IdRol}/Company/${CompanyId}`), model);
    }

    deleteRoleCompany(IdRol, CompanyId)  {
        return this._http.delete(this.api.getUrl(`api/Roles/${IdRol}?CompanyId=${CompanyId}`));
    }

    deleteRole(Id)  {
        return this._http.delete(this.api.getUrl(`api/Roles/${Id}`));
    }

    postClaimsAddToRole(model)  {
        return this._http.post(this.api.getUrl(`api/Claims/AddToRole`), model);
    }

   postCloneRole(model)  {
        return this._http.post(this.api.getUrl(`api/Roles/Clone`), model);
   }

    /**
     * Services modulo references // COMMONOPTIONS
     */

    getCommonOptionsListAll()  {
        return this._http.get(this.api.getUrl(`api/CommonOptions`));
    }

    getCommonOptionsId(id)  {
        return this._http.get(this.api.getUrl(`api/CommonOptions/${id}`));
    }

    putCommonOptionsId(id, model)  {
        return this._http.put(this.api.getUrl(`api/CommonOptions/${id}`), model);
    }

    deleteCommonOptionsId(id)  {
        return this._http.delete(this.api.getUrl(`api/CommonOptions/${id}`));
    }

    postCommonOption(model)  {
        return this._http.post(this.api.getUrl(`api/CommonOptions`), model);
    }

    /**
     * Services Requirement ALL
     */

    getRequirementAll()  {
        return this._http.get(this.api.getUrl(`api/Requirement/GetAll`));
    }

    getRequirementId(Id)  {
        return this._http.get(this.api.getUrl(`api/Requirement/${Id}`));
    }

    deleteRequirementId(id) {
        return this._http.delete(this.api.getUrl(`api/Requirement/${id}`));
    }

    putRequirementId(id, model)  {
        return this._http.put(this.api.getUrl(`api/Requirement/${id}`), model);
    }

    postRequirement(model)  {
        return this._http.post(this.api.getUrl(`api/Requirement`), model);
    }

    /**
     * Services Actividades economicas
     */

     getEconomicActivityAll()  {
        return this._http.get(this.api.getUrl(`api/EconomicActivity`));
    }

    getEconomicActivityId(Id)  {
        return this._http.get(this.api.getUrl(`api/EconomicActivity/${Id}`));
    }

    deleteEconomicActivityId(id) {
        return this._http.delete(this.api.getUrl(`api/EconomicActivity/${id}`));
    }

    putEconomicActivityId(id, model)  {
        return this._http.put(this.api.getUrl(`api/EconomicActivity/${id}`), model);
    }

    postEconomicActivity(model)  {
        return this._http.post(this.api.getUrl(`api/EconomicActivity`), model);
    }

    /**
     * Services Security References
     */

    getSecurityReferencesAll()  {
        return this._http.get(this.api.getUrl(`api/SecurityReferences`));
    }

    getSecurityReferencesId(Id)  {
        return this._http.get(this.api.getUrl(`api/SecurityReferences/${Id}`));
    }

    deleteSecurityReferencesId(id) {
        return this._http.delete(this.api.getUrl(`api/SecurityReferences/${id}`));
    }

    putSecurityReferencesId(id, model)  {
        return this._http.put(this.api.getUrl(`api/SecurityReferences/${id}`), model);
    }

    postSecurityReferences(model)  {
        return this._http.post(this.api.getUrl(`api/SecurityReferences`), model);
    }

    putSecurityReferencesUpdateFileId(id, filedata)  {
        return this._http.put(this.api.getUrl(`api/SecurityReferences/UpdateFile/${id}`), filedata);
    }


    /**
     * Services Form Image
     */

    getFormImage(){
        return this._http.get<any>(this.api.getUrl(`api/FormImage`));
    }

    getFormImageId(id){
        return this._http.get<any>(this.api.getUrl(`api/FormImage/${id}`));
    }

    putFormImageId(id, model){
        return this._http.put<any>(this.api.getUrl(`api/FormImage/${id}`), model);
    }

    deleteFormImageId(id) {
        return this._http.delete(this.api.getUrl(`api/FormImage/${id}`));
    }

    postFormImage(model)  {
        return this._http.post(this.api.getUrl(`api/FormImage`), model);
    }

    putFormImageUpdateFileId(id, filedata)  {
        return this._http.put(this.api.getUrl(`api/FormImage/UpdateFile/${id}`), filedata);
    }

    /**
     * Services modulo reference occupation
     */

    getJobAll(){
        return this._http.get<any>(this.api.getUrl(`api/Job`));
    }

    getJobId(id){
        return this._http.get<any>(this.api.getUrl(`api/Job/${id}`));
    }

    putJobId(id, model){
        this.clearJobsLocal();
        return this._http.put<any>(this.api.getUrl(`api/Job/${id}`), model);
    }

    deleteJobId(id) {
        this.clearJobsLocal();
        return this._http.delete(this.api.getUrl(`api/Job/${id}`));
    }

    postJob(model)  {
        this.clearJobsLocal();
        return this._http.post(this.api.getUrl(`api/Job`), model);
    }

    /**
     * Services modulo reference SocialSecRange
     */

    getSocialSecRangeAll(){
        return this._http.get<any>(this.api.getUrl(`api/SocialSecRange`));
    }

    getSocialSecRangeId(id){
        return this._http.get<any>(this.api.getUrl(`api/SocialSecRange/${id}`));
    }

    putSocialSecRangeId(id, model){
        return this._http.put<any>(this.api.getUrl(`api/SocialSecRange/${id}`), model);
    }

    deleteSocialSecRangeId(id) {
        return this._http.delete(this.api.getUrl(`api/SocialSecRange/${id}`));
    }

    postSocialSecRange(model)  {
        return this._http.post(this.api.getUrl(`api/SocialSecRange`), model);
    }

    /**
     * Services modulo reference grupos de insumos
     */
    getSupplyGroup(){
        return this._http.get<any>(this.api.getUrl(`api/Company/SupplyGroup`));
    }

    getSupplyGroupId(id){
        return this._http.get<any>(this.api.getUrl(`api/Company/SupplyGroup/${id}`));
    }

    putSupplyGroupId(id, model){
        return this._http.put<any>(this.api.getUrl(`api/Company/SupplyGroup/${id}`), model);
    }

    deleteSupplyGroupId(id) {
        return this._http.delete(this.api.getUrl(`api/Company/SupplyGroup/${id}`));
    }

    postSupplyGroup(model)  {
        return this._http.post(this.api.getUrl(`api/Company/SupplyGroup`), model);
    }

    putSupplyGroupUpdateFileId(id, filedata)  {
        return this._http.put(this.api.getUrl(`api/Company/SupplyGroup/Photo/${id}`), filedata);
    }

    /**
     * Services modulo reference lista de insumos
     */
    getSupply(){
        return this._http.get<any>(this.api.getUrl(`api/Company/Supply?IsActive=true`));
    }

    getSupplyId(id){
        return this._http.get<any>(this.api.getUrl(`api/Company/Supply/${id}`));
    }

    putSupplyId(id, model){
        return this._http.put<any>(this.api.getUrl(`api/Company/Supply/${id}`), model);
    }

    deleteSupplyId(id) {
        return this._http.delete(this.api.getUrl(`api/Company/Supply/${id}`));
    }

    postSupply(model)  {
        return this._http.post(this.api.getUrl(`api/Company/Supply`), model);
    }

    // history
    getHistory(id?) {
        return this._http.get<any>(this.api.getUrl(`api/Personal/Historical/Contract/${id}`));
    }

    // Modulo perfiles days expire password change
    getRoleConfigurationByAdmin(rolId) {
        return this._http.get<any>(this.api.getUrl(`api/Company/RoleConfigurationByAdmin/rol/${rolId}`));
    }

    postRoleConfigurationByAdmin(model)  {
        return this._http.post(this.api.getUrl(`api/Company/RoleConfigurationByAdmin`), model);
    }


    getCompanyByNitId(nit){
      return this._http.get<any>(this.api.getUrl(`api/Company/ByNitId/${nit}`));
    }

}
